import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPage = ({ theme }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--background)] pt-24 pb-16 px-6" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[var(--primary)] hover:underline mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        {/* Header */}
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-[var(--foreground)]">
          Privacy Policy
        </h1>
        <p className="text-[var(--muted)] mb-12">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' , year: 'numeric' })}
        </p>

        {/* Content */}
        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">1. Introduction</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              At Hotpeek, we take your privacy seriously. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you visit our website or use our 
              services. Please read this privacy policy carefully.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">2. Information We Collect</h2>
            <p className="text-[var(--muted)] leading-relaxed mb-4">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc pl-6 text-[var(--muted)] space-y-2">
              <li>Personal identification information (name, email address, phone number)</li>
              <li>Social media profiles and handles</li>
              <li>Photos and content you submit through our application form</li>
              <li>Communication records between you and our team</li>
              <li>Financial information necessary for payment processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">3. How We Use Your Information</h2>
            <p className="text-[var(--muted)] leading-relaxed mb-4">
              We use the information we collect in the following ways:
            </p>
            <ul className="list-disc pl-6 text-[var(--muted)] space-y-2">
              <li>To evaluate your application and determine eligibility for our services</li>
              <li>To provide and maintain our creator management services</li>
              <li>To communicate with you about our services and opportunities</li>
              <li>To process payments and manage your account</li>
              <li>To improve our services and develop new features</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">4. Information Sharing</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              We do not sell, trade, or rent your personal information to third parties. We may share 
              your information with trusted service providers who assist us in operating our business, 
              conducting our services, or servicing you. These parties are bound by confidentiality 
              agreements and are only permitted to use your information as necessary to provide services to us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">5. Data Security</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your 
              personal information against unauthorized access, alteration, disclosure, or destruction. 
              However, no method of transmission over the Internet or electronic storage is 100% secure, 
              and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">6. Data Retention</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes 
              outlined in this privacy policy, unless a longer retention period is required or 
              permitted by law. When we no longer need your information, we will securely delete 
              or anonymize it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">7. Your Rights</h2>
            <p className="text-[var(--muted)] leading-relaxed mb-4">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 text-[var(--muted)] space-y-2">
              <li>The right to access your personal information</li>
              <li>The right to correct inaccurate information</li>
              <li>The right to delete your personal information</li>
              <li>The right to restrict or object to processing</li>
              <li>The right to data portability</li>
              <li>The right to withdraw consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">8. Cookies and Tracking</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our website and 
              hold certain information. You can instruct your browser to refuse all cookies or to 
              indicate when a cookie is being sent. However, if you do not accept cookies, you may 
              not be able to use some portions of our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">9. Third-Party Links</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the 
              privacy practices or content of these third-party sites. We encourage you to read the 
              privacy policy of every website you visit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">10. Children's Privacy</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              Our services are not intended for individuals under the age of 18. We do not knowingly 
              collect personal information from children. If you are a parent or guardian and believe 
              your child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">11. Changes to This Policy</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date. 
              You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">12. Contact Us</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please 
              contact us at privacy@hotpeek.com or through our application form.
            </p>
          </section>
        </div>

        {/* Footer Note */}
        <div className={`mt-16 p-6 rounded-xl ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'}`}>
          <p className="text-[var(--muted)] text-sm">
            <strong className="text-[var(--foreground)]">Note:</strong> This is placeholder content. 
            Please consult with a legal professional to create a privacy policy specific to your 
            business operations, data practices, and applicable privacy regulations (GDPR, CCPA, etc.).
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
